import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AuthState } from './types';
import { doGetUserProfile, doPinLoginRequest } from './userAPI';
import {
  clearAuthToken,
  getAuthToken,
  notifyErrorToBugsnag,
  setAuthToken,
} from '../../utils';
import storage from '../../utils/storage';
import Bugsnag from '@bugsnag/js';

// User Profile
export const getUserProfile = createAsyncThunk(
  'auth/getUserProfile',
  async () => {
    return await doGetUserProfile();
  },
);

// PIN Login Process
export const doPinLogin = createAsyncThunk(
  'auth/login',
  async ({ username, pin }: { username: string; pin: string }) => {
    return await doPinLoginRequest(username, pin);
  },
);

const USERNAME = '@wavelight/username';

const initialState: AuthState = {
  login: {
    username: storage.get(USERNAME) || '',
    pin: '',
  },
  session: {
    recoveringSession: true,
    logged: false,
    access_token: '',
  },
  profile: null,
  instantLogin: {
    pinRequested: false,
  },
  isLoading: false,
  isProfileLoading: false,
};

export const captureSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Realod Login from LocalStorage
    initializeLogin: (state) => {
      const token = getAuthToken();
      if (token) {
        state.session.access_token = token;
        state.session.logged = true;
      }
      state.session.recoveringSession = false;
    },
    doLogout: (state) => {
      state.login = initialState.login;
      state.profile = initialState.profile;
      state.session = initialState.session;
      clearAuthToken();
    },
    // Login
    setUsername: (state, action: PayloadAction<string>) => {
      state.login.username = action.payload;
      storage.set(USERNAME, action.payload);
    },
    setPinPassword: (state, action: PayloadAction<string>) => {
      state.login.pin = action.payload;
    },
    // Instant Login
    setInstantLogin: (state, action: PayloadAction<boolean>) => {
      state.instantLogin.pinRequested = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Sucessfull login attempt
    builder.addCase(doPinLogin.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(doPinLogin.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.session.access_token = payload.access_token;
      state.session.logged = true;
      state.instantLogin.pinRequested = false;
      setAuthToken(payload.access_token);
    });
    builder.addCase(doPinLogin.rejected, (state, action) => {
      state.isLoading = false;
      notifyErrorToBugsnag(state, action);
    });
    // Get user Profile
    builder.addCase(getUserProfile.pending, (state) => {
      state.isProfileLoading = true;
    });
    builder.addCase(getUserProfile.fulfilled, (state, { payload }) => {
      state.profile = payload;
      state.isProfileLoading = false;
      Bugsnag.setUser(payload?.userId, payload?.email, payload?.username);
    });
    builder.addCase(getUserProfile.rejected, (state, action) => {
      state.session.logged = false;
      state.isProfileLoading = false;
      notifyErrorToBugsnag(state, action);
    });
  },
});

export const {
  // Realod Session
  initializeLogin,
  doLogout,
  // Login
  setUsername,
  setPinPassword,
  // Instant Login
  setInstantLogin,
} = captureSlice.actions;

export default captureSlice.reducer;
